import React, { useMemo, useState } from "react";
import {
  Bot,
  CheckCircle,
  ChevronDown,
  MessageCircle,
  Send,
  User,
  X,
} from "lucide-react";

const API_BASE_URL = "https://teamamtbsolution.com/api";
const WHATSAPP_NUMBER = "918431960546";

const services = [
  "Web Application Development",
  "Mobile App Development",
  "SaaS Product Development",
  "AI Tools Development",
  "Cloud Solutions",
  "Team Augmentation",
  "SEO & Digital Services",
  "Other",
];

const initialMessages = [
  {
    sender: "bot",
    text: "Hi, welcome to AMTB Solution. Share your requirement and contact details, and our team will get back to you.",
  },
];

const FloatingContactWidget = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState(initialMessages);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    requirement: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const whatsappUrl = useMemo(() => {
    const text = encodeURIComponent(
      "Hi AMTB Solution, I want to discuss a project requirement."
    );
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
  }, []);

  const addMessage = (sender, text) => {
    setMessages((prev) => [...prev, { sender, text }]);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (status === "sending") return;

    if (!formData.name.trim() || !formData.email.trim() || !formData.requirement.trim()) {
      setError("Please enter your name, email, and requirement.");
      return;
    }

    setError("");
    setStatus("sending");

    const userSummary = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      formData.phone ? `Phone: ${formData.phone}` : null,
      formData.service ? `Service: ${formData.service}` : null,
      `Requirement: ${formData.requirement}`,
    ].filter(Boolean).join("\n");

    const transcript = [
      ...messages,
      {
        sender: "user",
        text: userSummary,
      },
    ];

    try {
      const response = await fetch(`${API_BASE_URL}/chatbot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          page: window.location.pathname,
          transcript,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Unable to send your requirement.");
      }

      setMessages([
        ...transcript,
        {
          sender: "bot",
          text: "Thank you. Your requirement has been sent to our team.",
        },
      ]);
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        requirement: "",
      });
      setStatus("sent");
    } catch (err) {
      setError(err.message || "Unable to send your requirement.");
      setStatus("idle");
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3">
      {isChatOpen && (
        <div className="w-[calc(100vw-2.5rem)] max-w-sm overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Bot size={20} />
              <div>
                <p className="text-sm font-semibold">AMTB Assistant</p>
                <p className="text-xs text-white/80">Requirement support</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsChatOpen(false)}
              className="rounded-full p-1 hover:bg-white/15"
              aria-label="Close chatbot"
            >
              <X size={18} />
            </button>
          </div>

          <div className="max-h-56 space-y-3 overflow-y-auto bg-slate-50 px-4 py-3">
            {messages.map((message, index) => (
              <div
                key={`${message.sender}-${index}`}
                className={`flex ${message.sender === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[82%] whitespace-pre-line rounded-2xl px-3 py-2 text-sm ${
                    message.sender === "user"
                      ? "bg-[#2D6A6A] text-white"
                      : "bg-white text-gray-700 shadow-sm"
                  }`}
                >
                  {message.text}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 p-4">
            {status === "sent" && (
              <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
                <CheckCircle size={16} />
                Requirement sent successfully.
              </div>
            )}

            {error && (
              <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Name *"
                  className="h-10 w-full rounded-lg border border-gray-200 pl-9 pr-3 text-sm outline-none focus:border-[#B8336A]"
                />
              </div>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone"
                className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#B8336A]"
              />
            </div>

            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email *"
              className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#B8336A]"
            />

            <div className="relative">
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                className="h-10 w-full appearance-none rounded-lg border border-gray-200 bg-white px-3 pr-9 text-sm outline-none focus:border-[#B8336A]"
              >
                <option value="">Service interested in</option>
                {services.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>

            <textarea
              name="requirement"
              value={formData.requirement}
              onChange={handleChange}
              placeholder="Tell us your requirement *"
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#B8336A]"
            />

            <button
              type="submit"
              disabled={status === "sending"}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-sm font-semibold text-white transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Send size={16} />
              {status === "sending" ? "Sending..." : "Send Requirement"}
            </button>
          </form>
        </div>
      )}

      <div className="flex flex-col items-end gap-3">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noreferrer"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl transition hover:scale-105 hover:shadow-2xl"
          aria-label="Contact on WhatsApp"
          title="Contact on WhatsApp"
        >
          <MessageCircle size={27} />
        </a>

        <button
          type="button"
          onClick={() => setIsChatOpen((value) => !value)}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white shadow-xl transition hover:scale-105 hover:shadow-2xl"
          aria-label={isChatOpen ? "Close chatbot" : "Open chatbot"}
          title={isChatOpen ? "Close chatbot" : "Open chatbot"}
        >
          {isChatOpen ? <X size={25} /> : <Bot size={26} />}
        </button>
      </div>
    </div>
  );
};

export default FloatingContactWidget;
